import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-login-card.css?inline';
import '../pk-card/pk-card.js';

/**
 * `<pk-login-card>` — the full login form surface for auth-style pages.
 * Wraps a `<pk-card>` and provides email + password fields, a primary submit
 * button (with loading state), an error alert slot, and a foot-links slot.
 *
 * The form submits via standard browser POST so server-side controllers can
 * handle it without JavaScript. On submit, the JS flips the button to its
 * loading state for immediate visual feedback.
 *
 * Attributes:
 *   - `action`      — form POST URL (default: /portal/login)
 *   - `error`       — error message string; renders a pk-alert above the form when set
 *   - `email`       — pre-filled email value
 *   - `csrf-token`  — anti-forgery token value. When set, renders a hidden input
 *                     inside the form so server-side CSRF validation passes.
 *   - `csrf-name`   — name attribute for the hidden input. Defaults to
 *                     `__RequestVerificationToken` (the ASP.NET Core convention).
 *   - `eyebrow`     — small uppercase brand label above the heading.
 *                     Default: `ActivityLog`. Set this for non-ActivityLog products.
 *   - `title`       — main heading. Default: `Sign in`.
 *   - `lede`        — paragraph beneath the heading. Default copy describes the
 *                     ActivityLog portal; override for other products.
 *
 * Slots:
 *   - `foot` — links beneath the form ("No account? Register" / "Forgot password?")
 */
export class PkLoginCard extends HTMLElement {
    static readonly tagName = 'pk-login-card';

    static get observedAttributes(): string[] {
        return [
            'action', 'error', 'email',
            'csrf-token', 'csrf-name',
            'eyebrow', 'title', 'lede',
        ];
    }

    private readonly _root: ShadowRoot;
    private _submitting = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this._root.addEventListener('submit', this._handleSubmit);
        // Enter-to-submit. Real keyboard events from the inner <input> in
        // pk-form-field's nested shadow root are composed and bubble out
        // through this host element; listen here rather than on _root.
        this.addEventListener('keydown', this._handleKeydown);
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._handleSubmit);
        this.removeEventListener('keydown', this._handleKeydown);
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _handleSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        // Only intercept our own form (not slots)
        if (!form.classList.contains('login-form')) return;
        this._submitting = true;
        this.render();
        // Let the browser submit continue (no e.preventDefault())
    };

    /**
     * Enter-to-submit. The form's inputs live in `<pk-form-field>`'s shadow
     * root, so the browser's native implicit-submit (Enter inside a form)
     * does not fire across the shadow boundary. We listen for Enter at the
     * host and trigger the same submit code path the button uses.
     *
     * Loading state, an empty value (the inputs are `required`, so a native
     * form submit will short-circuit on the constraint), and any outer-form
     * double-submit are all handled here.
     */
    private readonly _handleKeydown = (e: KeyboardEvent): void => {
        if (e.key !== 'Enter') return;
        // Only respond when focus is inside one of our form fields.
        const inField = e.composedPath().some(
            (n) => n instanceof Element && n.tagName === 'PK-FORM-FIELD',
        );
        if (!inField) return;
        // Always preventDefault so an outer <form> wrapping <pk-login-card>
        // does not also submit (avoids double-fire).
        e.preventDefault();
        if (this._submitting) return;
        const form = this._root.querySelector('form.login-form') as HTMLFormElement | null;
        if (!form) return;
        // Respect the submit button's disabled gate (mirrors clicking it).
        const submitBtn = this._root.querySelector(
            'pk-button[type="submit"]',
        ) as HTMLElement | null;
        if (submitBtn?.hasAttribute('disabled')) return;
        // Mirror clicking the submit button: requestSubmit fires the submit
        // event (so _handleSubmit can flip the loading state) and also
        // performs validation + native POST in real browsers. Fall back to
        // dispatching a submit event manually if requestSubmit is missing
        // (older browsers, or test environments — jsdom does not navigate).
        if (typeof form.requestSubmit === 'function') {
            try { form.requestSubmit(); } catch {
                form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
        } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    };

    private get _action(): string {
        return this.getAttribute('action') ?? '/portal/login';
    }

    private get _error(): string {
        return this.getAttribute('error') ?? '';
    }

    private get _email(): string {
        return this.getAttribute('email') ?? '';
    }

    private get _csrfToken(): string {
        return this.getAttribute('csrf-token') ?? '';
    }

    private get _csrfName(): string {
        return this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
    }

    private get _eyebrow(): string {
        return this.getAttribute('eyebrow') ?? 'ActivityLog';
    }

    private get _title(): string {
        return this.getAttribute('title') ?? 'Sign in';
    }

    private get _lede(): string {
        return this.getAttribute('lede')
            ?? 'Sign in to your portal to view messages, manage systems, and configure retention.';
    }

    private template(): TemplateResult {
        const action      = this._action;
        const error       = this._error;
        const email       = this._email;
        const csrfToken   = this._csrfToken;
        const csrfName    = this._csrfName;
        const eyebrow     = this._eyebrow;
        const heading     = this._title;
        const lede        = this._lede;
        const submitting  = this._submitting;

        return html`
            <style>${styles}</style>
            <pk-card>
                <span slot="eyebrow" class="eyebrow">${eyebrow}</span>
                <h1>${heading}</h1>
                <p class="lede">${lede}</p>

                ${error ? html`
                    <pk-alert variant="error" class="form-error">${error}</pk-alert>
                ` : ''}

                <form
                    class="login-form"
                    method="post"
                    action=${action}
                    novalidate
                >
                    ${csrfToken ? html`
                        <input type="hidden" name=${csrfName} .value=${csrfToken} />
                    ` : ''}
                    <pk-form-field
                        name="email"
                        type="email"
                        label="Email address"
                        autocomplete="username"
                        .value=${email}
                        required
                    ></pk-form-field>

                    <pk-form-field
                        name="password"
                        type="password"
                        label="Password"
                        autocomplete="current-password"
                        required
                    ></pk-form-field>

                    <pk-button
                        variant="primary"
                        type="submit"
                        class="submit-btn"
                        ?data-submitting=${submitting}
                        loading-label="Signing in…"
                        ?disabled=${submitting}
                        aria-busy=${submitting ? 'true' : 'false'}
                    >Sign in</pk-button>
                </form>

                <div slot="foot">
                    <slot name="foot">
                        <span>No account? <a href="/portal/register">Register free</a></span>
                        &ensp;·&ensp;
                        <a href="/portal/forgot-password">Forgot password?</a>
                    </slot>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkLoginCard.tagName)) {
    customElements.define(PkLoginCard.tagName, PkLoginCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-login-card': PkLoginCard;
    }
}
